import { createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit';
import { tasksAPI } from '../../api/client';
import { MIN_COUNT, MAX_COUNT } from '../../components/taskConstructor/exerciseTypes';

// DD.MM.YYYY
const formatDate = (date) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${date.getFullYear()}`;
};

const defaultTitle = () => `Новое Задание ${formatDate(new Date())}`;

// На фронте состав задания храним как словарь { [код типа]: количество },
// а с бэком обмениваемся списком [{ type, count }] (только ненулевые).
const exercisesFromServer = (list) =>
    Array.isArray(list)
        ? Object.fromEntries(list.filter((e) => e && e.count > 0).map((e) => [e.type, e.count]))
        : {};

const exercisesToServer = (map) =>
    Object.entries(map)
        .filter(([, count]) => count > 0)
        .map(([type, count]) => ({ type, count }));

/*
 * Задание в состоянии:
 *   key        — локальный ключ для React/выбора (у несохранённого задания ещё нет id)
 *   id         — UUID с бэка; null, пока задание не сохранено
 *   title      — название (может совпадать у разных заданий)
 *   savedTitle — название, сохранённое на бэке (чтобы понять, есть ли изменения)
 *   exercises  — { [код типа]: количество }; у сохранённого задания не меняется
 *   isDirty    — есть несохранённые изменения
 */
const fromServer = (task) => ({
    key: `srv-${task.id}`,
    id: task.id,
    title: task.title ?? '',
    savedTitle: task.title ?? '',
    exercises: exercisesFromServer(task.exercises),
    isDirty: false,
});

const makeNewTask = () => ({
    key: `new-${nanoid()}`,
    id: null,
    title: defaultTitle(),
    savedTitle: null,
    exercises: {},
    isDirty: true,
});

export const fetchTasks = createAsyncThunk('tasks/fetchTasks', async () => {
    const response = await tasksAPI.getAll();
    return response.data;
});

// Новое задание -> POST (бэк возвращает id). Сохранённое -> PATCH только названия.
export const saveTask = createAsyncThunk('tasks/saveTask', async (key, { getState }) => {
    const task = getState().tasks.items.find((t) => t.key === key);

    if (task.id == null) {
        const response = await tasksAPI.create({
            title: task.title,
            exercises: exercisesToServer(task.exercises),
        });
        if (!response.data || !response.data.id) {
            throw new Error('Бэкенд не вернул id задания');
        }
        return response.data;
    }

    const response = await tasksAPI.updateTitle(task.id, task.title);
    return response.data;
});

const tasksSlice = createSlice({
    name: 'tasks',
    initialState: {
        items: [],
        selectedKey: null,
        panel: 'editor', // что показано справа: 'editor' | 'stats'
        loadStatus: 'idle', // idle | loading | succeeded | failed
        savingKey: null,
        saveError: null,
    },
    reducers: {
        createTask: {
            reducer(state, action) {
                state.items.unshift(action.payload);
                state.selectedKey = action.payload.key;
                state.panel = 'editor';
                state.saveError = null;
            },
            prepare() {
                return { payload: makeNewTask() };
            },
        },
        // Новое (несохранённое) задание с тем же составом, что у сохранённого
        createFromTemplate: {
            reducer(state, action) {
                const { sourceKey, task } = action.payload;
                const source = state.items.find((t) => t.key === sourceKey);
                if (!source || source.id == null) return;
                task.exercises = { ...source.exercises };
                state.items.unshift(task);
                state.selectedKey = task.key;
                state.panel = 'editor';
                state.saveError = null;
            },
            prepare(sourceKey) {
                return { payload: { sourceKey, task: makeNewTask() } };
            },
        },
        selectTask(state, action) {
            state.selectedKey = action.payload;
            state.panel = 'editor';
            state.saveError = null;
        },
        openStats(state, action) {
            state.selectedKey = action.payload;
            state.panel = 'stats';
            state.saveError = null;
        },
        renameTask(state, action) {
            const { key, title } = action.payload;
            const task = state.items.find((t) => t.key === key);
            if (!task) return;
            task.title = title;
            task.isDirty = task.id == null ? true : title !== task.savedTitle;
        },
        // count: число 1..10, либо null/0 — тип убирается из задания
        setExerciseCount(state, action) {
            const { key, type, count } = action.payload;
            const task = state.items.find((t) => t.key === key);
            if (!task || task.id != null) return; // сохранённое задание менять нельзя
            if (!count || count < MIN_COUNT) {
                delete task.exercises[type];
            } else {
                task.exercises[type] = Math.min(MAX_COUNT, Math.floor(count));
            }
            task.isDirty = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTasks.pending, (state) => {
                state.loadStatus = 'loading';
            })
            .addCase(fetchTasks.fulfilled, (state, action) => {
                state.loadStatus = 'succeeded';
                const unsaved = state.items.filter((t) => t.id == null);
                const localById = new Map(state.items.filter((t) => t.id != null).map((t) => [t.id, t]));
                const fromBackend = (Array.isArray(action.payload) ? action.payload : []).map((raw) => {
                    const task = fromServer(raw);
                    const local = localById.get(task.id);
                    if (local) {
                        task.key = local.key; // чтобы не сбить выбор
                        if (local.isDirty) {   // не терять несохранённое переименование
                            task.title = local.title;
                            task.isDirty = true;
                        }
                    }
                    return task;
                });
                state.items = [...unsaved, ...fromBackend];
            })
            .addCase(fetchTasks.rejected, (state) => {
                state.loadStatus = 'failed';
            })
            .addCase(saveTask.pending, (state, action) => {
                state.savingKey = action.meta.arg;
                state.saveError = null;
            })
            .addCase(saveTask.fulfilled, (state, action) => {
                const task = state.items.find((t) => t.key === action.meta.arg);
                const data = action.payload || {};
                if (task) {
                    if (task.id == null) task.id = data.id;
                    if (data.title != null) task.title = data.title;
                    if (Array.isArray(data.exercises)) task.exercises = exercisesFromServer(data.exercises);
                    task.savedTitle = task.title;
                    task.isDirty = false;
                }
                state.savingKey = null;
            })
            .addCase(saveTask.rejected, (state) => {
                state.savingKey = null;
                state.saveError = 'Не удалось сохранить задание. Проверьте, что бэкенд запущен.';
            });
    },
});

export const {
    createTask,
    createFromTemplate,
    selectTask,
    openStats,
    renameTask,
    setExerciseCount,
} = tasksSlice.actions;
export default tasksSlice.reducer;
